/**
 * jsonMergeUtil_MergeHelpers.ts - Array and placeholder merge helpers.
 * Port of C# JsonMergeUtil_MergeHelpers.cs.
 */

import { TemplateOperation, JsonArrayPlaceholder, JsonKeyPlaceholder } from '../../model/index.js';
import { TemplateOperationKind } from '../../model/index.js';
import { indexOfCaseInsensitive, lastIndexOfCaseInsensitive, replaceAllCaseInsensitive } from './jsonMergeUtil_Replace.js';
import { tryGetTruthy, tryGetStrictBool } from './jsonMergeUtil_Boolean.js';
import { handleConditional } from './jsonMergeUtil_Conditional.js';
import { toPlaceholderString, tryGetObjectArrayItem } from './jsonMergeUtil_Scalar.js';
import { JsonValue } from '../../app/json/jsonValue.js';
import { JsonArray } from '../../app/json/jsonArray.js';
import { JsonObject } from '../../app/json/jsonObject.js';

const ESCAPED_DOUBLE_BRACE = '{\u200B{';

// Resolves an array item value into a JsonObject. Scalars are wrapped as { "Value": scalar }
// so templates can reference {{$Value}} uniformly regardless of array shape.
function asArrayItemObject(item: JsonValue): JsonObject {
    const objResult = tryGetObjectArrayItem(item);
    if (objResult.found && objResult.obj !== null) {
        return objResult.obj;
    }
    const wrapped = new JsonObject();
    wrapped.set('Value', item);
    return wrapped;
}

export function applySkipConditionalMarkers(
    template: string,
    templateKey: string,
    currentTemplateName: string,
    operations: TemplateOperation[] | null,
    defaultSkipJson: string
): string {
    let result = template;
    let scanPos = 0;
    while (scanPos < result.length) {
        const skipStart = result.indexOf('{{!?', scanPos);
        if (skipStart === -1) break;

        const skipTagEnd = result.indexOf('}}', skipStart);
        if (skipTagEnd === -1) break;

        const skipKey = result.substring(skipStart + 4, skipTagEnd);
        const skipBlockEndTag = '{{/' + skipKey + '}}';
        const skipBlockEndIdx = indexOfCaseInsensitive(result.substring(skipTagEnd + 2), skipBlockEndTag);

        if (skipBlockEndIdx !== -1) {
            const absoluteSkipBlockEndIdx = skipTagEnd + 2 + skipBlockEndIdx;
            const negativeBlockStart = '{{^' + skipKey + '}}';
            const negativeBlockStartIdx = indexOfCaseInsensitive(result.substring(absoluteSkipBlockEndIdx + skipBlockEndTag.length), negativeBlockStart);

            if (negativeBlockStartIdx !== -1 && negativeBlockStartIdx < 100) {
                const betweenContent = result.substring(absoluteSkipBlockEndIdx + skipBlockEndTag.length, absoluteSkipBlockEndIdx + skipBlockEndTag.length + negativeBlockStartIdx);
                if (betweenContent.trim() === '') {
                    const absoluteNegativeBlockStartIdx = absoluteSkipBlockEndIdx + skipBlockEndTag.length + negativeBlockStartIdx;
                    const negativeBlockEnd = '{{/' + skipKey + '}}';
                    const negativeBlockEndIdx = indexOfCaseInsensitive(result.substring(absoluteNegativeBlockStartIdx + negativeBlockStart.length), negativeBlockEnd);

                    if (negativeBlockEndIdx !== -1) {
                        const absoluteNegativeBlockEndIdx = absoluteNegativeBlockStartIdx + negativeBlockStart.length + negativeBlockEndIdx;
                        result = result.substring(0, skipStart) + defaultSkipJson + result.substring(absoluteNegativeBlockEndIdx + negativeBlockEnd.length);

                        if (operations !== null) {
                            operations.push(new TemplateOperation({
                                templateName: templateKey,
                                kind: TemplateOperationKind.ConditionalSkip,
                                target: '{{!?' + skipKey + '}}',
                                replacedWith: `${currentTemplateName}_${skipKey} (skipped)`,
                                operations: []
                            }));
                        }

                        continue;
                    }
                }
            }

            result = result.substring(0, skipStart) + defaultSkipJson + result.substring(absoluteSkipBlockEndIdx + skipBlockEndTag.length);

            if (operations !== null) {
                operations.push(new TemplateOperation({
                    templateName: templateKey,
                    kind: TemplateOperationKind.ConditionalSkip,
                    target: '{{!?' + skipKey + '}}',
                    replacedWith: `${currentTemplateName}_${skipKey} (skipped)`,
                    operations: []
                }));
            }

            continue;
        }

        scanPos = skipTagEnd + 2;
    }

    return result;
}

export function applySkipArrayMarkers(
    template: string,
    templateKey: string,
    currentTemplateName: string,
    operations: TemplateOperation[] | null,
    defaultSkipJson: string
): string {
    let result = template;
    let scanPos = 0;
    while (scanPos < result.length) {
        const skipArrayStart = result.indexOf('{{!@', scanPos);
        if (skipArrayStart === -1) break;

        const skipArrayTagEnd = result.indexOf('}}', skipArrayStart);
        if (skipArrayTagEnd === -1) break;

        const skipKey = result.substring(skipArrayStart + 4, skipArrayTagEnd);
        const skipBlockEndTag = '{{/' + skipKey + '}}';
        const skipBlockEndIdx = indexOfCaseInsensitive(result.substring(skipArrayTagEnd + 2), skipBlockEndTag);

        if (skipBlockEndIdx !== -1) {
            const absoluteSkipBlockEndIdx = skipArrayTagEnd + 2 + skipBlockEndIdx;
            const negativeBlockStart = '{{^' + skipKey + '}}';
            const negativeBlockStartIdx = indexOfCaseInsensitive(result.substring(absoluteSkipBlockEndIdx + skipBlockEndTag.length), negativeBlockStart);

            if (negativeBlockStartIdx !== -1 && negativeBlockStartIdx < 100) {
                const betweenContent = result.substring(absoluteSkipBlockEndIdx + skipBlockEndTag.length, absoluteSkipBlockEndIdx + skipBlockEndTag.length + negativeBlockStartIdx);
                if (betweenContent.trim() === '') {
                    const absoluteNegativeBlockStartIdx = absoluteSkipBlockEndIdx + skipBlockEndTag.length + negativeBlockStartIdx;
                    const negativeBlockEnd = '{{/' + skipKey + '}}';
                    const negativeBlockEndIdx = indexOfCaseInsensitive(result.substring(absoluteNegativeBlockStartIdx + negativeBlockStart.length), negativeBlockEnd);

                    if (negativeBlockEndIdx !== -1) {
                        const absoluteNegativeBlockEndIdx = absoluteNegativeBlockStartIdx + negativeBlockStart.length + negativeBlockEndIdx;
                        result = result.substring(0, skipArrayStart) + defaultSkipJson + result.substring(absoluteNegativeBlockEndIdx + negativeBlockEnd.length);

                        if (operations !== null) {
                            operations.push(new TemplateOperation({
                                templateName: templateKey,
                                kind: TemplateOperationKind.ArraySkip,
                                target: '{{!@' + skipKey + '}}',
                                replacedWith: `${currentTemplateName}_${skipKey} (skipped)`,
                                operations: []
                            }));
                        }

                        continue;
                    }
                }
            }

            result = result.substring(0, skipArrayStart) + defaultSkipJson + result.substring(absoluteSkipBlockEndIdx + skipBlockEndTag.length);

            if (operations !== null) {
                operations.push(new TemplateOperation({
                    templateName: templateKey,
                    kind: TemplateOperationKind.ArraySkip,
                    target: '{{!@' + skipKey + '}}',
                    replacedWith: `${currentTemplateName}_${skipKey} (skipped)`,
                    operations: []
                }));
            }

            continue;
        }

        scanPos = skipArrayTagEnd + 2;
    }

    return result;
}

export function applyStandaloneConditionals(
    template: string,
    merged: JsonObject,
    templateKey: string,
    currentTemplateName: string,
    operations: TemplateOperation[] | null
): string {
    let result = template;

    for (const key of merged.keys()) {
        const value = merged.get(key);
        const truthy = tryGetTruthy(value);
        if (truthy.found) {
            result = handleConditional(result, key, truthy.result, templateKey, currentTemplateName, operations);
            const strict = tryGetStrictBool(value);
            if (strict.found) {
                result = handleArrayConditional(result, key, strict.result);
            }
        }
    }

    return result;
}

function handleArrayConditional(input: string, key: string, condition: boolean): string {
    const posStart = '{{@' + key + '}}';
    const posEnd = '{{/' + key + '}}';
    const negStart = '{{^' + key + '}}';
    const negEnd = '{{/' + key + '}}';

    let result = input;
    let posStartIdx = indexOfCaseInsensitive(result, posStart);
    while (posStartIdx >= 0) {
        const posEndIdx = indexOfCaseInsensitive(result, posEnd, posStartIdx + posStart.length);
        if (posEndIdx < 0) break;

        const afterPosEnd = posEndIdx + posEnd.length;
        const negStartIdx = indexOfCaseInsensitive(result, negStart, afterPosEnd);
        if (negStartIdx >= 0 && negStartIdx <= afterPosEnd + 100) {
            const between = result.substring(afterPosEnd, negStartIdx);
            if (between.trim() === '') {
                const negEndIdx = indexOfCaseInsensitive(result, negEnd, negStartIdx + negStart.length);
                if (negEndIdx >= 0) {
                    const posContent = result.substring(posStartIdx + posStart.length, posEndIdx);
                    const negContent = result.substring(negStartIdx + negStart.length, negEndIdx);
                    result = result.substring(0, posStartIdx)
                        + (condition ? posContent : negContent)
                        + result.substring(negEndIdx + negEnd.length);
                    posStartIdx = indexOfCaseInsensitive(result, posStart, posStartIdx);
                    continue;
                }
            }
        }

        const content = result.substring(posStartIdx + posStart.length, posEndIdx);
        result = condition
            ? result.substring(0, posStartIdx) + content + result.substring(posEndIdx + posEnd.length)
            : result.substring(0, posStartIdx) + result.substring(posEndIdx + posEnd.length);
        posStartIdx = indexOfCaseInsensitive(result, posStart, posStartIdx);
    }

    return result;
}

export function collectArrayBlocksInTemplate(template: string, arrayMetadata: JsonArrayPlaceholder[] | null): Set<string> {
    const arrayBlocksInTemplate = new Set<string>();

    if (arrayMetadata != null) {
        for (const arrayPlaceholder of arrayMetadata) {
            arrayBlocksInTemplate.add(arrayPlaceholder.arrayName);
        }
    } else {
        let arrayScanPos = 0;
        while (arrayScanPos < template.length) {
            const arrayStart = template.indexOf('{{@', arrayScanPos);
            if (arrayStart === -1) break;

            const arrayTagEnd = template.indexOf('}}', arrayStart);
            if (arrayTagEnd === -1) break;

            const key = template.substring(arrayStart + 3, arrayTagEnd);
            if (!key.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
                arrayBlocksInTemplate.add(key);
            }
            arrayScanPos = arrayTagEnd + 2;
        }
    }

    return arrayBlocksInTemplate;
}

export function buildArrayMetadataLookup(arrayMetadata: JsonArrayPlaceholder[] | null): Map<string, JsonArrayPlaceholder> | null {
    if (arrayMetadata == null || arrayMetadata.length === 0) {
        return null;
    }

    const metadataLookup = new Map<string, JsonArrayPlaceholder>();
    for (const arrayPlaceholder of arrayMetadata) {
        if (arrayPlaceholder && arrayPlaceholder.arrayName) {
            metadataLookup.set(arrayPlaceholder.arrayName.toLowerCase(), arrayPlaceholder);
        }
    }

    return metadataLookup;
}

function isInsideFalseConditional(template: string, position: number, merged: JsonObject): boolean {
    let searchPos = position;
    while (searchPos > 0) {
        const condStart = template.lastIndexOf('{{?', searchPos - 1);
        if (condStart === -1) break;

        const condTagEnd = template.indexOf('}}', condStart + 3);
        if (condTagEnd === -1) break;

        const condKey = template.substring(condStart + 3, condTagEnd);
        const closeTag = '{{/' + condKey + '}}';
        const closeIdx = indexOfCaseInsensitive(template, closeTag, condTagEnd + 2);

        if (closeIdx !== -1 && closeIdx > position) {
            const lookup = merged.tryGetValue(condKey);
            if (lookup.found) {
                const truthy = tryGetTruthy(lookup.value);
                if (truthy.found && !truthy.result) return true;
            }
            return false;
        }

        searchPos = condStart;
    }
    return false;
}

function replaceMissingArrayItemKeys(
    itemBlock: string,
    templateKey: string,
    currentTemplateName: string,
    arrayOperations: TemplateOperation[],
    trackOps: boolean,
    defaultReplaceHtml: string
): string {
    let missingKeyPos = 0;
    let result = itemBlock;
    while (missingKeyPos < result.length) {
        const start = result.indexOf('{{$', missingKeyPos);
        if (start === -1) break;
        const endOffset = result.indexOf('}}', start);
        if (endOffset === -1) break;
        const missingKey = result.substring(start + 3, endOffset);
        if (!missingKey.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
            const missingPlaceholder = '{{$' + missingKey + '}}';
            if (trackOps) {
                arrayOperations.push(new TemplateOperation({
                    templateName: `${templateKey}_ArrayItem`,
                    kind: TemplateOperationKind.KeyMissing,
                    target: missingPlaceholder,
                    replacedWith: `${currentTemplateName}_${missingKey}`,
                    operations: []
                }));
            }
            result = replaceAllCaseInsensitive(result, missingPlaceholder, defaultReplaceHtml);
            continue;
        }
        missingKeyPos = endOffset + 2;
    }
    return result;
}

function processArrayItems(
    dataList: JsonArray,
    blockContent: string,
    conditionalKeys: Set<string>,
    templateKey: string,
    currentTemplateName: string,
    arrayOperations: TemplateOperation[],
    trackOps: boolean,
    maxArrayOperationItems: number,
    defaultReplaceHtml: string,
    defaultSkipJson: string
): string {
    const mergedParts: string[] = [];
    let itemIndex = 0;

    for (let i = 0; i < dataList.length; i++) {
        const rawItem = dataList.get(i);
        const item = asArrayItemObject(rawItem);
        let itemBlock = blockContent;

        for (const key of item.keys()) {
            const skipPlaceholder = '{{!$' + key + '}}';
            if (indexOfCaseInsensitive(itemBlock, skipPlaceholder) !== -1) {
                itemBlock = replaceAllCaseInsensitive(itemBlock, skipPlaceholder, defaultSkipJson);
            }
        }

        // Expand nested array blocks declared in this item's template (e.g. {{@RoleOptions}} inside a
        // {{@SecurityUsers}} item). Runs on the pristine block before {{$key}} substitution so only
        // predefined template structure is scanned; nested op logging is gated by the outer item index
        // like the outer KeyReplace ops. Matches the C# reference.
        if (itemBlock.includes('{{@')) {
            const nestedArrayBlocks = collectArrayBlocksInTemplate(itemBlock, null);
            if (nestedArrayBlocks.size > 0) {
                const nestedOps = (trackOps && itemIndex < maxArrayOperationItems) ? arrayOperations : null;
                itemBlock = processJsonArrayBlocks(itemBlock, item, nestedArrayBlocks, null, templateKey, currentTemplateName, nestedOps, maxArrayOperationItems, defaultReplaceHtml, defaultSkipJson);

                // Must run even when the item carries NO array value at all — that is the case it
                // exists for. A nested {{@Inner}} whose key is absent from the item is an unresolved
                // array block exactly like a top-level one: block (plus an adjacent paired {{^Inner}})
                // replaced by the fallback, one ArrayIteration "(array not found)" in the item's ops.
                // processJsonArrayBlocks has already removed every key it resolved, so what is left in
                // the set is exactly the unmatched ones.
                itemBlock = replaceMissingArrayBlocks(itemBlock, item, nestedArrayBlocks, templateKey, currentTemplateName, nestedOps, defaultReplaceHtml);
            }
        }

        for (const key of item.keys()) {
            const jsonValue = item.get(key);
            const placeholder = '{{$' + key + '}}';
            const valueStr = toPlaceholderString(jsonValue);

            if (trackOps && itemIndex < maxArrayOperationItems) {
                if (indexOfCaseInsensitive(itemBlock, placeholder) !== -1) {
                    arrayOperations.push(new TemplateOperation({
                        templateName: `${templateKey}_ArrayItem`,
                        kind: TemplateOperationKind.KeyReplace,
                        target: placeholder,
                        replacedWith: `ArrayItem_${key}`,
                        operations: []
                    }));
                }
            }

            const safeValueStr = valueStr.includes('{{') ? valueStr.replace(/\{\{/g, ESCAPED_DOUBLE_BRACE) : valueStr;
            itemBlock = replaceAllCaseInsensitive(itemBlock, placeholder, safeValueStr);
        }

        itemBlock = replaceMissingArrayItemKeys(
            itemBlock,
            templateKey,
            currentTemplateName,
            arrayOperations,
            trackOps && itemIndex < maxArrayOperationItems,
            defaultReplaceHtml
        );

        itemIndex++;

        for (const condKey of conditionalKeys) {
            let condValue = false;
            const condLookup = item.tryGetValue(condKey);
            if (condLookup.found) {
                // An ARRAY-valued key is truthy exactly when it has items — the same rule the
                // top-level empty-array pass applies. tryGetStrictBool rejects an array outright and
                // leaves the value false, which made a {{^Inner}} inside an array item show its
                // negative branch even when the nested array was FULL.
                if (condLookup.value.isArray) {
                    condValue = condLookup.value.getArray().length > 0;
                } else {
                    const strict = tryGetStrictBool(condLookup.value);
                    if (strict.found) condValue = strict.result;
                }
            }
            itemBlock = handleConditional(itemBlock, condKey, condValue);
        }
        mergedParts.push(itemBlock);
    }

    return mergedParts.join('');
}

function resolveNegativeBlock(
    result: string,
    startIdx: number,
    blockStartTag: string,
    blockEndTag: string,
    tag: string,
    metadata: JsonArrayPlaceholder | null,
    dataListCount: number
): { hasNegativeBlock: boolean; negativeContent: string; fullReplacement: string; isValid: boolean } {
    if (metadata != null && metadata.hasNegativeBlock && metadata.isPairedNegative) {
        const negativeContent = dataListCount === 0 ? metadata.negativeBlockContent : '';
        return { hasNegativeBlock: true, negativeContent, fullReplacement: metadata.fullMatchWithNegative, isValid: true };
    }

    const searchFrom = startIdx + blockStartTag.length;
    const endIdx = indexOfCaseInsensitive(result, blockEndTag, searchFrom);
    if (endIdx === -1) {
        return { hasNegativeBlock: false, negativeContent: '', fullReplacement: '', isValid: false };
    }

    const negativeBlockStart = '{{^' + tag + '}}';
    const negativeBlockEnd = '{{/' + tag + '}}';
    const afterMainBlock = endIdx + blockEndTag.length;
    const negativeStartIdx = indexOfCaseInsensitive(result, negativeBlockStart, afterMainBlock);

    if (negativeStartIdx !== -1 && (negativeStartIdx - afterMainBlock) < 100) {
        const between = result.substring(afterMainBlock, negativeStartIdx);
        if (between.trim() === '') {
            const negativeEndIdx = indexOfCaseInsensitive(result, negativeBlockEnd, negativeStartIdx + negativeBlockStart.length);
            if (negativeEndIdx !== -1) {
                const negativeContent = dataListCount === 0
                    ? result.substring(negativeStartIdx + negativeBlockStart.length, negativeEndIdx)
                    : '';
                return {
                    hasNegativeBlock: true,
                    negativeContent,
                    fullReplacement: result.substring(startIdx, negativeEndIdx + negativeBlockEnd.length),
                    isValid: true
                };
            }
        }
    }

    return {
        hasNegativeBlock: false,
        negativeContent: '',
        fullReplacement: result.substring(startIdx, endIdx + blockEndTag.length),
        isValid: true
    };
}

export function processJsonArrayBlocks(
    template: string,
    merged: JsonObject,
    arrayBlocksInTemplate: Set<string>,
    metadataLookup: Map<string, JsonArrayPlaceholder> | null,
    templateKey: string,
    currentTemplateName: string,
    operations: TemplateOperation[] | null,
    maxArrayOperationItems: number,
    defaultReplaceHtml: string,
    defaultSkipJson: string
): string {
    let result = template;

    for (const jsonKey of merged.keys()) {
        const jsonValue = merged.get(jsonKey);
        if (jsonValue.isArray) {
            const dataList = jsonValue.getArray();
            const keyNorm = jsonKey.toLowerCase();
            const possibleTags = [jsonKey, jsonKey.toLowerCase(), keyNorm.replace(/s+$/, ''), keyNorm + 's'];

            for (const tag of possibleTags) {
                let metadata: JsonArrayPlaceholder | null = null;
                if (metadataLookup != null) {
                    metadata = metadataLookup.get(tag.toLowerCase()) ?? null;
                }

                const blockStartTag = '{{@' + tag + '}}';
                const blockEndTag = '{{/' + tag + '}}';
                const startIdx = indexOfCaseInsensitive(result, blockStartTag);
                if (startIdx === -1) continue;

                if (isInsideFalseConditional(result, startIdx, merged)) {
                    continue;
                }

                let blockContent: string;
                let conditionalKeys: Set<string>;

                if (metadata != null) {
                    blockContent = metadata.blockContent;
                    conditionalKeys = new Set<string>(metadata.nestedArrays || []);
                } else {
                    const searchFrom = startIdx + blockStartTag.length;
                    const endIdx = indexOfCaseInsensitive(result, blockEndTag, searchFrom);

                    if (endIdx === -1 || endIdx <= startIdx) continue;

                    const contentStartIdx = startIdx + blockStartTag.length;
                    if (contentStartIdx > endIdx) continue;

                    blockContent = result.substring(contentStartIdx, endIdx);
                    conditionalKeys = new Set<string>();
                    let condIdx = 0;
                    while (true) {
                        const condStart = indexOfCaseInsensitive(blockContent, '{{?', condIdx);
                        const negCondStart = indexOfCaseInsensitive(blockContent, '{{^', condIdx);
                        let actualStart: number;
                        if (condStart === -1 && negCondStart === -1) break;
                        else if (condStart === -1) actualStart = negCondStart;
                        else if (negCondStart === -1) actualStart = condStart;
                        else actualStart = Math.min(condStart, negCondStart);
                        const condEnd = indexOfCaseInsensitive(blockContent, '}}', actualStart);
                        if (condEnd === -1) break;
                        const condKey = blockContent.substring(actualStart + 3, condEnd).trim();
                        conditionalKeys.add(condKey);
                        condIdx = condEnd + 2;
                    }
                }

                const arrayOperations: TemplateOperation[] = [];
                const trackOps = operations !== null && templateKey !== '' && currentTemplateName !== '';
                const mergedBlock = processArrayItems(
                    dataList,
                    blockContent,
                    conditionalKeys,
                    templateKey,
                    currentTemplateName,
                    arrayOperations,
                    trackOps,
                    maxArrayOperationItems,
                    defaultReplaceHtml,
                    defaultSkipJson
                );

                const { hasNegativeBlock, negativeContent, fullReplacement, isValid } = resolveNegativeBlock(
                    result,
                    startIdx,
                    blockStartTag,
                    blockEndTag,
                    tag,
                    metadata,
                    dataList.length
                );
                if (!isValid) {
                    continue;
                }

                const replaceIdx = indexOfCaseInsensitive(result, fullReplacement);
                if (replaceIdx !== -1) {
                    result = result.substring(0, replaceIdx) + mergedBlock + negativeContent + result.substring(replaceIdx + fullReplacement.length);
                }

                if (operations !== null && templateKey !== '' && currentTemplateName !== '') {
                    if (dataList.length === 0 && hasNegativeBlock) {
                        operations.push(new TemplateOperation({
                            templateName: templateKey,
                            kind: TemplateOperationKind.NegativeCondition,
                            target: '{{^' + jsonKey + '}}',
                            replacedWith: '(shown)',
                            operations: []
                        }));
                    } else {
                        const loggedCount = dataList.length < maxArrayOperationItems ? dataList.length : maxArrayOperationItems;
                        const replacedWith = `${currentTemplateName}_${jsonKey} (${dataList.length} items, ${loggedCount} logged)`;

                        operations.push(new TemplateOperation({
                            templateName: templateKey,
                            kind: TemplateOperationKind.ArrayIteration,
                            target: blockStartTag,
                            replacedWith,
                            operations: arrayOperations
                        }));
                    }
                }

                for (const existingKey of arrayBlocksInTemplate) {
                    if (existingKey.toLowerCase() === tag.toLowerCase()) {
                        arrayBlocksInTemplate.delete(existingKey);
                        break;
                    }
                }

                break;
            }
        }
    }

    return result;
}

function isInsideAnyArrayBlock(keyPh: JsonKeyPlaceholder, arrayMetadata: JsonArrayPlaceholder[]): boolean {
    for (const arrayPh of arrayMetadata) {
        if (keyPh.startIndex >= arrayPh.startIndex && keyPh.endIndex <= arrayPh.endIndex) {
            return true;
        }
    }
    return false;
}

export function processSimplePlaceholders(
    template: string,
    merged: JsonObject,
    templateKey: string,
    currentTemplateName: string,
    operations: TemplateOperation[] | null,
    defaultReplaceHtml: string,
    defaultSkipJson: string,
    jsonKeyMetadata: JsonKeyPlaceholder[] | null = null,
    arrayMetadata: JsonArrayPlaceholder[] | null = null
): string {
    let result = template;

    // Fast path: skip the whole per-key loop when no {{!$ marker exists at all
    // (mirrors csharp JsonMergeUtil_MergeHelpers.cs:856 `if (result.Contains("{{!$", Ordinal))`).
    // Replacements only remove markers, never add them, so this guard is stable.
    if (result.includes('{{!$')) {
        for (const key of merged.keys()) {
            const skipPlaceholder = '{{!$' + key + '}}';
            if (indexOfCaseInsensitive(result, skipPlaceholder) !== -1) {
                result = replaceAllCaseInsensitive(result, skipPlaceholder, defaultSkipJson);

                if (operations && templateKey && currentTemplateName) {
                    operations.push(new TemplateOperation({
                        templateName: templateKey,
                        kind: TemplateOperationKind.KeySkip,
                        target: skipPlaceholder,
                        replacedWith: `${currentTemplateName}_${key}`,
                        operations: []
                    }));
                }
            }
        }
    }

    const simplePlaceholdersInTemplate = new Set<string>();
    if (jsonKeyMetadata != null) {
        // Metadata path (PreProcess engine): loader already excluded HTMLPLACEHOLDER entries and
        // validated keyName, so the list mirrors what the dynamic scan produced for the original
        // template content. Placeholders whose original-template positions fall inside an array
        // block are consumed by per-item array iteration, so they are excluded here to keep the
        // missing-key log identical to the legacy dynamic scan that ran on `result` after array processing.
        for (const keyPh of jsonKeyMetadata) {
            if (arrayMetadata != null && isInsideAnyArrayBlock(keyPh, arrayMetadata)) continue;
            simplePlaceholdersInTemplate.add(keyPh.keyName);
        }
    } else {
        // Dynamic scan (Normal engine path or when metadata not available).
        let placeholderScanPos = 0;
        while (placeholderScanPos < result.length) {
            const placeholderStart = result.indexOf('{{$', placeholderScanPos);
            if (placeholderStart === -1) break;

            const placeholderEnd = result.indexOf('}}', placeholderStart);
            if (placeholderEnd === -1) break;

            const key = result.substring(placeholderStart + 3, placeholderEnd);
            if (!key.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
                simplePlaceholdersInTemplate.add(key);
            }
            placeholderScanPos = placeholderEnd + 2;
        }
    }

    for (const key of merged.keys()) {
        const jsonValue = merged.get(key);
        // Arrays are handled by processJsonArrayBlocks — skip them here.
        if (jsonValue.isArray) continue;

        const valueStr = toPlaceholderString(jsonValue);
        const placeholder = '{{$' + key + '}}';

        if (operations !== null && templateKey !== '' && currentTemplateName !== '') {
            if (indexOfCaseInsensitive(result, placeholder) !== -1) {
                operations.push(new TemplateOperation({
                    templateName: templateKey,
                    kind: TemplateOperationKind.KeyReplace,
                    target: placeholder,
                    replacedWith: `${currentTemplateName}_${key}`,
                    operations: []
                }));
            }
        }

        const safeValueStr = valueStr.includes('{{') ? valueStr.replace(/\{\{/g, ESCAPED_DOUBLE_BRACE) : valueStr;
        result = replaceAllCaseInsensitive(result, placeholder, safeValueStr);
        for (const existingKey of simplePlaceholdersInTemplate) {
            if (existingKey.toLowerCase() === key.toLowerCase()) {
                simplePlaceholdersInTemplate.delete(existingKey);
                break;
            }
        }
    }

    for (const unmatchedKey of simplePlaceholdersInTemplate) {
        const placeholder = '{{$' + unmatchedKey + '}}';
        result = replaceAllCaseInsensitive(result, placeholder, defaultReplaceHtml);

        if (operations !== null && templateKey !== '' && currentTemplateName !== '') {
            operations.push(new TemplateOperation({
                templateName: templateKey,
                kind: TemplateOperationKind.KeyMissing,
                target: placeholder,
                replacedWith: `${currentTemplateName}_${unmatchedKey}`,
                operations: []
            }));
        }
    }

    return result;
}

export function replaceMissingArrayBlocks(
    template: string,
    merged: JsonObject,
    arrayBlocksInTemplate: Set<string>,
    templateKey: string,
    currentTemplateName: string,
    operations: TemplateOperation[] | null,
    defaultReplaceHtml: string
): string {
    let result = template;
    const processedNegativeBlocks = new Set<string>();
    for (const unmatchedKey of arrayBlocksInTemplate) {
        const matched = merged.tryGetValue(unmatchedKey);
        if (matched.found && matched.value.isBool) {
            continue;
        }

        const blockStartTag = '{{@' + unmatchedKey + '}}';
        const blockEndTag = '{{/' + unmatchedKey + '}}';

        const startIdx = indexOfCaseInsensitive(result, blockStartTag);
        if (startIdx !== -1) {
            // A block inside a FALSE conditional is skipped by processJsonArrayBlocks (matching the
            // JSON engines, which never traverse an unselected conditional body), so it arrives here
            // still unresolved and the JSON engines log no ArrayIteration for it. Replace the block
            // anyway so its inner placeholders do not leak into the later passes, but suppress the
            // op: the conditional pass removes the whole body afterwards, so neither belongs in the
            // trace (the Metrics FailedOperations ops drift).
            const logMissingArrayOp = !isInsideFalseConditional(result, startIdx, merged);
            const endIdx = indexOfCaseInsensitive(result, blockEndTag, startIdx + blockStartTag.length);
            if (endIdx !== -1) {
                const negativeBlockStart = '{{^' + unmatchedKey + '}}';
                const negativeBlockEnd = '{{/' + unmatchedKey + '}}';
                const afterMainBlock = endIdx + blockEndTag.length;
                const negativeStartIdx = indexOfCaseInsensitive(result.substring(afterMainBlock), negativeBlockStart);

                if (negativeStartIdx !== -1 && negativeStartIdx < 100) {
                    const between = result.substring(afterMainBlock, afterMainBlock + negativeStartIdx);
                    if (between.trim() === '') {
                        const absoluteNegativeStartIdx = afterMainBlock + negativeStartIdx;
                        const negativeEndIdx = indexOfCaseInsensitive(result.substring(absoluteNegativeStartIdx + negativeBlockStart.length), negativeBlockEnd);
                        if (negativeEndIdx !== -1) {
                            const absoluteNegativeEndIdx = absoluteNegativeStartIdx + negativeBlockStart.length + negativeEndIdx;
                            result = result.substring(0, startIdx) + defaultReplaceHtml + result.substring(absoluteNegativeEndIdx + negativeBlockEnd.length);
                            processedNegativeBlocks.add(unmatchedKey.toLowerCase());

                            if (logMissingArrayOp && operations !== null && templateKey !== '' && currentTemplateName !== '') {
                                operations.push(new TemplateOperation({
                                    templateName: templateKey,
                                    kind: TemplateOperationKind.ArrayIteration,
                                    target: blockStartTag,
                                    replacedWith: `${defaultReplaceHtml} (array not found)`,
                                    operations: []
                                }));
                            }

                            continue;
                        }
                    }
                }

                result = result.substring(0, startIdx) + defaultReplaceHtml + result.substring(endIdx + blockEndTag.length);

                // Log it exactly as the paired branch above does. Only the paired branch used to log,
                // so a missing array with no {{^Key}} beside it was replaced SILENTLY.
                if (logMissingArrayOp && operations !== null && templateKey !== '' && currentTemplateName !== '') {
                    operations.push(new TemplateOperation({
                        templateName: templateKey,
                        kind: TemplateOperationKind.ArrayIteration,
                        target: blockStartTag,
                        replacedWith: `${defaultReplaceHtml} (array not found)`,
                        operations: []
                    }));
                }
            }
        }
    }

    for (const unmatchedKey of arrayBlocksInTemplate) {
        if (processedNegativeBlocks.has(unmatchedKey.toLowerCase())) continue;
        const matched = merged.tryGetValue(unmatchedKey);
        if (matched.found && matched.value.isBool) continue;

        const negBlockStart = '{{^' + unmatchedKey + '}}';
        const negBlockEnd = '{{/' + unmatchedKey + '}}';

        const negStartIdx = indexOfCaseInsensitive(result, negBlockStart);
        if (negStartIdx !== -1) {
            const negEndIdx = indexOfCaseInsensitive(result, negBlockEnd, negStartIdx + negBlockStart.length);
            if (negEndIdx !== -1) {
                result = result.substring(0, negStartIdx) + defaultReplaceHtml + result.substring(negEndIdx + negBlockEnd.length);
            }
        }
    }

    for (const key of merged.keys()) {
        const value = merged.get(key);
        if (!value.isArray) continue;
        const emptyBlockStart = '{{^' + key + '}}';
        const emptyBlockEnd = '{{/' + key + '}}';
        const emptyStartIdx = indexOfCaseInsensitive(result, emptyBlockStart);
        const emptyEndIdx = indexOfCaseInsensitive(result, emptyBlockEnd);

        if (emptyStartIdx !== -1 && emptyEndIdx !== -1) {
            const arrayValue = value.getArray();
            const positiveBlockEnd = '{{/' + key + '}}';
            const searchStart = Math.max(0, emptyStartIdx - 100);
            const positiveEndIdx = lastIndexOfCaseInsensitive(result, positiveBlockEnd, emptyStartIdx, emptyStartIdx - searchStart);

            if (positiveEndIdx !== -1) {
                const between = result.substring(positiveEndIdx + positiveBlockEnd.length, emptyStartIdx);
                if (between.trim() === '') {
                    continue;
                }
            }

            const isEmpty = arrayValue.length === 0;
            const emptyContent = result.substring(emptyStartIdx + emptyBlockStart.length, emptyEndIdx);
            result = isEmpty
                ? result.substring(0, emptyStartIdx) + emptyContent + result.substring(emptyEndIdx + emptyBlockEnd.length)
                : result.substring(0, emptyStartIdx) + result.substring(emptyEndIdx + emptyBlockEnd.length);
        }
    }

    return result;
}
